import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '../api';
import { solscanAccountUrl } from '../network';

const PROFESSIONS = [
    "Accommodation & Food Services",
    "Accounting & Tax Services",
    "Ambulatory / Outpatient Care Services",
    "Behavioral Health & Substance Abuse Services",
    "Business & Management Services",
    "Construction",
    "Educational Services",
    "Engineering & Architecture",
    "Finance & Banking",
    "Home Healthcare Services",
    "Hospitals",
    "Information Technology & Software",
    "Insurance",
    "Legal Services",
    "Manufacturing",
    "Marketing, Advertising & Creative Services",
    "Nursing & Residential Care Facilities",
    "Other Health Practitioners",
    "Real Estate & Property Management",
    "Retail Trade",
    "Scientific Research & Technical Testing",
    "Social Assistance (Non-Medical)",
    "Transportation",
    "Warehousing & Logistics",
    "Other Industry",
];

// --- Shared styles (dark theme) ---
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };
const thStyle = {
    padding: '10px 12px', textAlign: 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.4)', fontWeight: 600, whiteSpace: 'nowrap',
};
const tdStyle = { padding: '9px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'middle', color: 'rgba(255,255,255,0.85)' };
const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)',
    color: '#ffffff', fontSize: '0.88rem', boxSizing: 'border-box',
    outline: 'none',
};
const btnStyle = (variant = 'default') => ({
    padding: '5px 12px', borderRadius: '6px', border: 'none',
    cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
    ...(variant === 'danger'   ? { background: 'rgba(220,53,69,0.2)', color: '#ff6b6b', border: '1px solid rgba(220,53,69,0.3)' } :
        variant === 'success'  ? { background: 'rgba(40,167,69,0.2)', color: '#4dd880', border: '1px solid rgba(40,167,69,0.3)' } :
        variant === 'warning'  ? { background: 'rgba(255,193,7,0.15)', color: '#FFC107', border: '1px solid rgba(255,193,7,0.3)' } :
        variant === 'primary'  ? { background: 'rgba(99,179,255,0.15)', color: '#63b3ff', border: '1px solid rgba(99,179,255,0.3)' } :
        variant === 'muted'    ? { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' } :
                                 { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.12)' }),
});

// --- MODAL ---
function Modal({ title, onClose, children }) {
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px',
            backdropFilter: 'blur(4px)',
        }}>
            <div style={{
                background: 'linear-gradient(135deg, rgba(26,26,46,0.98) 0%, rgba(18,18,26,0.98) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '28px', width: '100%', maxWidth: '500px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#fff' }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}>✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}

// --- EDIT USER MODAL ---
function EditUserModal({ user, onSave, onClose }) {
    const [form, setForm] = useState({
        name: user.name || '',
        legal_name: user.legal_name || '',
        email: user.email || '',
        profession: user.profession || '',
    });
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const handle = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const res = await api.put(`/admin/users/${user.id}`, { ...form });
            onSave(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save changes');
        }
        setSaving(false);
    };

    const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' };

    return (
        <Modal title={`Edit User #${user.id}`} onClose={onClose}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                    <label style={labelStyle}>Display Name</label>
                    <input style={inputStyle} value={form.name} onChange={handle('name')} required placeholder="Display name" />
                </div>
                <div>
                    <label style={labelStyle}>Legal Name</label>
                    <input style={inputStyle} value={form.legal_name} onChange={handle('legal_name')} placeholder="Full legal name" />
                </div>
                <div>
                    <label style={labelStyle}>Email Address</label>
                    <input style={inputStyle} type="email" value={form.email} onChange={handle('email')} placeholder="Email" />
                </div>
                <div>
                    <label style={labelStyle}>Wallet Address (read-only — set by user via Phantom/Solflare)</label>
                    {user.wallet_address ? (
                        <code style={{ ...inputStyle, display: 'block', color: 'rgba(255,255,255,0.5)', cursor: 'default', userSelect: 'all' }}>
                            {user.wallet_address}
                        </code>
                    ) : (
                        <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.3)' }}>No wallet connected</span>
                    )}
                </div>
                <div>
                    <label style={labelStyle}>Profession / Industry</label>
                    <select style={inputStyle} value={form.profession} onChange={handle('profession')}>
                        <option value="">— None / Not set —</option>
                        {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <small style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>Admin override — user normally can only set this once.</small>
                </div>
                {error && <p style={{ margin: 0, color: '#ff6b6b', fontSize: '0.85rem' }}>{error}</p>}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <button type="submit" style={btnStyle('success')} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
                    <button type="button" style={btnStyle()} onClick={onClose}>Cancel</button>
                </div>
            </form>
        </Modal>
    );
}

// --- RESET PASSWORD MODAL ---
function ResetPasswordModal({ user, onClose }) {
    const [newPassword, setNewPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 8) return setError('Password must be at least 8 characters.');
        if (newPassword !== confirm) return setError('Passwords do not match.');
        setSaving(true);
        try {
            await api.post(`/admin/users/${user.id}/reset-password`, { new_password: newPassword });
            setDone(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to reset password');
        }
        setSaving(false);
    };

    const passwordOk = newPassword.length === 0 || newPassword.length >= 8;
    const confirmOk  = confirm.length === 0 || confirm === newPassword;
    const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' };

    return (
        <Modal title={`Reset Password — ${user.legal_name || user.name || user.email}`} onClose={onClose}>
            {done ? (
                <div style={{ textAlign: 'center' }}>
                    <p style={{ color: '#4dd880', fontWeight: 600, fontSize: '1rem' }}>✔ Password reset successfully.</p>
                    <button style={btnStyle()} onClick={onClose}>Close</button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                        <label style={labelStyle}>
                            New password <span style={{ color: passwordOk ? 'rgba(255,255,255,0.3)' : '#ff6b6b' }}>(min. 8 characters)</span>
                        </label>
                        <input
                            style={{ ...inputStyle, borderColor: !passwordOk ? 'rgba(220,53,69,0.5)' : 'rgba(255,255,255,0.12)' }}
                            type="password" required autoComplete="new-password"
                            placeholder="••••••••"
                            value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Confirm new password</label>
                        <input
                            style={{ ...inputStyle, borderColor: !confirmOk ? 'rgba(220,53,69,0.5)' : 'rgba(255,255,255,0.12)' }}
                            type="password" required autoComplete="new-password"
                            placeholder="••••••••"
                            value={confirm} onChange={e => setConfirm(e.target.value)}
                        />
                        {!confirmOk && <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#ff6b6b' }}>Passwords do not match</p>}
                    </div>
                    {error && <p style={{ margin: 0, color: '#ff6b6b', fontSize: '0.85rem' }}>{error}</p>}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <button type="submit" style={btnStyle('warning')} disabled={saving}>{saving ? 'Resetting…' : 'Reset Password'}</button>
                        <button type="button" style={btnStyle()} onClick={onClose}>Cancel</button>
                    </div>
                </form>
            )}
        </Modal>
    );
}

// --- Helpers ---
function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ isActive }) {
    return (
        <span style={{
            padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700,
            background: isActive ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)',
            color: isActive ? '#4dd880' : '#ff6b6b',
        }}>
            {isActive ? 'Active' : 'Disabled'}
        </span>
    );
}

function RoleBadge({ role }) {
    return (
        <span style={{
            padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700,
            background: role === 'admin' ? 'rgba(255,193,7,0.2)' : 'rgba(255,255,255,0.08)',
            color: role === 'admin' ? '#FFC107' : 'rgba(255,255,255,0.45)',
        }}>
            {role}
        </span>
    );
}

// ---- USERS TAB ----
function UsersTab() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [resetUser, setResetUser] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load users');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const toggleRole = async (user) => {
        const newRole = user.role === 'admin' ? 'user' : 'admin';
        if (!window.confirm(`Set ${user.legal_name || user.name || user.email} as "${newRole}"?`)) return;
        try {
            const res = await api.put(`/admin/users/${user.id}/role`, { role: newRole });
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: res.data.role } : u));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update role');
        }
    };

    const toggleActive = async (user) => {
        const newState = !user.is_active;
        const label = newState ? 'enable' : 'disable';
        if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} account for "${user.legal_name || user.email}"?`)) return;
        try {
            const res = await api.put(`/admin/users/${user.id}/active`, { is_active: newState });
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: res.data.is_active } : u));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update account status');
        }
    };

    const resetKyc = async (user) => {
        if (!window.confirm(`Reset KYC status for "${user.legal_name || user.email}"? They will need to re-verify.`)) return;
        try {
            await api.post(`/admin/users/${user.id}/reset-kyc`);
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, kyc_status: null } : u));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to reset KYC');
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Permanently delete "${user.legal_name || user.email}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/users/${user.id}`);
            setUsers(prev => prev.filter(u => u.id !== user.id));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete user');
        }
    };

    const handleEditSave = (updated) => {
        setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
        setEditingUser(null);
    };

    if (loading) return <p style={{ color: 'rgba(255,255,255,0.4)', padding: '20px 0' }}>Loading users…</p>;
    if (error)   return <p style={{ color: '#ff6b6b', padding: '20px 0' }}>{error}</p>;

    return (
        <div>
            {/* Modals */}
            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onSave={handleEditSave}
                    onClose={() => setEditingUser(null)}
                />
            )}
            {resetUser && (
                <ResetPasswordModal
                    user={resetUser}
                    onClose={() => setResetUser(null)}
                />
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: '#fff' }}>Users ({users.length})</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={btnStyle('warning')} onClick={async () => {
                        if (!window.confirm('Sweep all inactive wallets? This will send balances (95% to General Hive, 5% to publisher) for all disabled accounts with a balance.')) return;
                        try {
                            const { data } = await api.post('/admin/sweep-abandoned');
                            alert(`Sweep complete: ${data.swept} swept, ${data.skipped} empty, ${data.failed} failed (${data.total} inactive total)`);
                        } catch (err) {
                            alert('Sweep failed: ' + (err.response?.data?.error || err.message));
                        }
                    }}>Sweep Inactive Wallets</button>
                    <button style={btnStyle()} onClick={fetchUsers}>Refresh</button>
                </div>
            </div>

            {/* Auto-disable notice */}
            <div style={{ background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>
                Accounts are automatically disabled after <strong style={{ color: 'rgba(255,255,255,0.75)' }}>1 year of inactivity</strong>. Admins can re-enable them below.
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>ID</th>
                            <th style={thStyle}>Legal Name</th>
                            <th style={thStyle}>Email / Username</th>
                            <th style={thStyle}>Profession</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>KYC</th>
                            <th style={thStyle}>Role</th>
                            <th style={thStyle}>Wallet</th>
                            <th style={thStyle}>Balance (USDC)</th>
                            <th style={thStyle}>Last Login</th>
                            <th style={thStyle}>Created</th>
                            <th style={thStyle}>Auth</th>
                            <th style={thStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                                <td style={tdStyle}>{u.id}</td>
                                <td style={tdStyle}>
                                    <div style={{ fontWeight: 500, color: '#fff' }}>{u.legal_name || <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>}</div>
                                    <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>{u.name}</div>
                                </td>
                                <td style={{ ...tdStyle, fontSize: '0.82rem' }}>{u.email || '—'}</td>
                                <td style={{ ...tdStyle, fontSize: '0.8rem', color: u.profession ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.3)' }}>
                                    {u.profession || '—'}
                                </td>
                                <td style={tdStyle}><StatusBadge isActive={u.is_active} /></td>
                                <td style={tdStyle}>
                                    {u.kyc_status === 'Approved'  && <span style={{ fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px', background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 700 }}>✔ Verified</span>}
                                    {u.kyc_status === 'Declined'  && <span style={{ fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px', background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 700 }}>✕ Declined</span>}
                                    {u.kyc_status && u.kyc_status !== 'Approved' && u.kyc_status !== 'Declined' && <span style={{ fontSize: '0.75rem', padding: '2px 7px', borderRadius: '10px', background: 'rgba(255,193,7,0.15)', color: '#ffc107', fontWeight: 700 }}>⏳ Pending</span>}
                                    {!u.kyc_status && <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem' }}>—</span>}
                                </td>
                                <td style={tdStyle}><RoleBadge role={u.role} /></td>
                                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {u.wallet_address ? (
                                        <a href={solscanAccountUrl(u.wallet_address)} target="_blank" rel="noopener noreferrer"
                                            title={u.wallet_address}
                                            style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}
                                            onMouseEnter={e => e.target.style.color = '#ffc107'}
                                            onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                                        >
                                            {u.wallet_address.slice(0, 6)}…{u.wallet_address.slice(-4)} ↗
                                        </a>
                                    ) : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>}
                                </td>
                                <td style={{ ...tdStyle, fontSize: '0.82rem', color: u.balance_sol != null ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.2)', fontVariantNumeric: 'tabular-nums' }}>
                                    {u.balance_sol != null ? `${u.balance_sol.toFixed(2)} USDC` : '—'}
                                </td>
                                <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>{fmtDate(u.last_login_at)}</td>
                                <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>{fmtDate(u.created_at)}</td>
                                <td style={tdStyle}>
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                        {u.has_password && <span style={{ fontSize: '0.72rem', padding: '1px 5px', borderRadius: '4px', background: 'rgba(99,179,255,0.15)', color: '#63b3ff' }}>PW</span>}
                                        {u.has_google && <span style={{ fontSize: '0.72rem', padding: '1px 5px', borderRadius: '4px', background: 'rgba(255,193,7,0.15)', color: '#FFC107' }}>G</span>}
                                    </div>
                                </td>
                                <td style={tdStyle}>
                                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                        <button style={btnStyle('primary')} onClick={() => setEditingUser(u)} title="Edit user">Edit</button>
                                        <button style={btnStyle('muted')} onClick={() => setResetUser(u)} title="Reset password">Reset PW</button>
                                        <button
                                            style={btnStyle(u.is_active ? 'danger' : 'success')}
                                            onClick={() => toggleActive(u)}
                                        >
                                            {u.is_active ? 'Disable' : 'Enable'}
                                        </button>
                                        <button
                                            style={btnStyle(u.role === 'admin' ? 'default' : 'warning')}
                                            onClick={() => toggleRole(u)}
                                        >
                                            {u.role === 'admin' ? '↓ User' : '↑ Admin'}
                                        </button>
                                        {u.kyc_status && <button style={btnStyle('warning')} onClick={() => resetKyc(u)}>Reset KYC</button>}
                                        <button style={btnStyle('danger')} onClick={() => deleteUser(u)}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
                                    No users found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---- POOLS TAB ----
const emptyPoolForm = { name: '', description: '', target_amount: '' };

function PoolsTab() {
    const [pools, setPools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newPool, setNewPool] = useState(emptyPoolForm);
    const [editing, setEditing] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [selected, setSelected] = useState(new Set());

    const fetchPools = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/admin/pools');
            setPools(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load pools');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchPools(); }, [fetchPools]);

    const createPool = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/admin/pools', newPool);
            setPools(prev => [...prev, res.data]);
            setNewPool(emptyPoolForm);
            setShowCreateForm(false);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create pool');
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        try {
            const res = await api.put(`/admin/pools/${editing.id}`, editing);
            setPools(prev => prev.map(p => p.id === editing.id ? res.data : p));
            setEditing(null);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update pool');
        }
    };

    const deletePool = async (p) => {
        if (!window.confirm(`Delete pool "${p.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/pools/${p.id}`);
            setPools(prev => prev.filter(x => x.id !== p.id));
            setSelected(prev => { const s = new Set(prev); s.delete(p.id); return s; });
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete pool');
        }
    };

    const toggleActive = async (p) => {
        try {
            const res = await api.put(`/admin/pools/${p.id}/active`, { is_active: !p.is_active });
            setPools(prev => prev.map(x => x.id === p.id ? res.data : x));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update pool status');
        }
    };

    const bulkSetActive = async (is_active) => {
        const ids = [...selected];
        if (!ids.length) return;
        const label = is_active ? 'enable' : 'disable';
        if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${ids.length} pool(s)?`)) return;
        try {
            await api.put('/admin/pools/bulk-active', { ids, is_active });
            setPools(prev => prev.map(p => selected.has(p.id) ? { ...p, is_active } : p));
            setSelected(new Set());
        } catch (err) {
            alert(err.response?.data?.error || `Failed to ${label} pools`);
        }
    };

    const poolType = (name) => {
        if (name.startsWith('State: '))    return 'state';
        if (name.startsWith('City: '))     return 'municipality';
        if (name.startsWith('Industry: ')) return 'industry';
        if (name.startsWith('Company: '))  return 'company';
        return 'other';
    };

    const visible = pools.filter(p => {
        if (filterStatus === 'active'   && !p.is_active) return false;
        if (filterStatus === 'disabled' &&  p.is_active) return false;
        if (filterType !== 'all' && poolType(p.name) !== filterType) return false;
        if (search) {
            const q = search.toLowerCase();
            return p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
        }
        return true;
    });

    const allVisibleSelected = visible.length > 0 && visible.every(p => selected.has(p.id));
    const someSelected = selected.size > 0;

    const toggleSelectAll = () => {
        if (allVisibleSelected) {
            setSelected(prev => { const s = new Set(prev); visible.forEach(p => s.delete(p.id)); return s; });
        } else {
            setSelected(prev => { const s = new Set(prev); visible.forEach(p => s.add(p.id)); return s; });
        }
    };

    const toggleSelect = (id) => {
        setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    };

    const formStyle = {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
    };

    if (loading) return <p style={{ color: 'rgba(255,255,255,0.4)', padding: '20px 0' }}>Loading pools…</p>;
    if (error)   return <p style={{ color: '#ff6b6b', padding: '20px 0' }}>{error}</p>;

    return (
        <div>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ margin: 0, color: '#fff' }}>Hives ({visible.length}{visible.length !== pools.length ? ` of ${pools.length}` : ''})</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={btnStyle()} onClick={fetchPools}>Refresh</button>
                    <button style={btnStyle('primary')} onClick={() => { setShowCreateForm(v => !v); setEditing(null); }}>
                        {showCreateForm ? 'Cancel' : '+ New Pool'}
                    </button>
                </div>
            </div>

            {/* Search + filter bar */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input
                    style={{ ...inputStyle, flex: 1, minWidth: '160px' }}
                    placeholder="Search pools…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {['all', 'active', 'disabled'].map(f => (
                    <button
                        key={f}
                        style={btnStyle(filterStatus === f ? 'primary' : 'default')}
                        onClick={() => setFilterStatus(f)}
                    >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                ))}
            </div>
            {/* Type filter */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', alignSelf: 'center', paddingLeft: '2px' }}>Type:</span>
                {[
                    { value: 'all',          label: 'All' },
                    { value: 'state',        label: 'State' },
                    { value: 'municipality', label: 'Municipality' },
                    { value: 'industry',     label: 'Industry' },
                    { value: 'company',      label: 'Company' },
                    { value: 'other',        label: 'Other' },
                ].map(f => (
                    <button
                        key={f.value}
                        style={btnStyle(filterType === f.value ? 'primary' : 'default')}
                        onClick={() => setFilterType(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Bulk action bar */}
            {someSelected && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', padding: '8px 12px', background: 'rgba(99,179,255,0.08)', borderRadius: '6px', border: '1px solid rgba(99,179,255,0.2)' }}>
                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>{selected.size} selected</span>
                    <button style={btnStyle('success')} onClick={() => bulkSetActive(true)}>Enable</button>
                    <button style={btnStyle('warning')} onClick={() => bulkSetActive(false)}>Disable</button>
                    <button style={btnStyle()} onClick={() => setSelected(new Set())}>Clear</button>
                </div>
            )}

            {showCreateForm && (
                <form onSubmit={createPool} style={formStyle}>
                    <h4 style={{ margin: '0 0 14px', color: '#fff' }}>New Pool</h4>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input style={inputStyle} placeholder="Pool name *" value={newPool.name} required onChange={e => setNewPool(p => ({ ...p, name: e.target.value }))} />
                        <input style={inputStyle} placeholder="Description" value={newPool.description} onChange={e => setNewPool(p => ({ ...p, description: e.target.value }))} />
                        <input style={inputStyle} placeholder="Target amount (USDC)" type="number" min="0" step="any" value={newPool.target_amount} onChange={e => setNewPool(p => ({ ...p, target_amount: e.target.value }))} />
                    </div>
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                        <button type="submit" style={btnStyle('success')}>Create Pool</button>
                        <button type="button" style={btnStyle()} onClick={() => setShowCreateForm(false)}>Cancel</button>
                    </div>
                </form>
            )}

            {editing && (
                <form onSubmit={saveEdit} style={{ ...formStyle, border: '1px solid rgba(255,193,7,0.25)' }}>
                    <h4 style={{ margin: '0 0 14px', color: '#fff' }}>Edit Pool #{editing.id}</h4>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <input style={inputStyle} placeholder="Pool name *" value={editing.name} required onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
                        <input style={inputStyle} placeholder="Description" value={editing.description || ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} />
                        <input style={inputStyle} placeholder="Target amount (USDC)" type="number" min="0" step="any" value={editing.target_amount || ''} onChange={e => setEditing(p => ({ ...p, target_amount: e.target.value }))} />
                    </div>
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                        <button type="submit" style={btnStyle('success')}>Save Changes</button>
                        <button type="button" style={btnStyle()} onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                </form>
            )}

            <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: '36px' }}>
                                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} title="Select all visible" />
                            </th>
                            <th style={thStyle}>ID</th>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Description</th>
                            <th style={thStyle}>Wallet</th>
                            <th style={thStyle}>Target (USDC)</th>
                            <th style={thStyle}>Current (USDC)</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(p => (
                            <tr key={p.id} style={{ opacity: p.is_active === false ? 0.45 : 1 }}>
                                <td style={tdStyle}>
                                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} />
                                </td>
                                <td style={tdStyle}>{p.id}</td>
                                <td title={p.name} style={{ ...tdStyle, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</td>
                                <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>{p.description || '—'}</td>
                                <td style={tdStyle}>
                                    {p.wallet_address ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <a href={solscanAccountUrl(p.wallet_address)} target="_blank" rel="noopener noreferrer"
                                                title={p.wallet_address}
                                                style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}
                                                onMouseEnter={e => e.target.style.color = '#ffc107'}
                                                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                                            >
                                                {p.wallet_address.slice(0, 6)}…{p.wallet_address.slice(-4)} ↗
                                            </a>
                                            <button
                                                style={{ ...btnStyle('muted'), padding: '1px 6px', fontSize: '0.7rem' }}
                                                onClick={() => navigator.clipboard.writeText(p.wallet_address)}
                                                title="Copy full address"
                                            >Copy</button>
                                        </span>
                                    ) : '—'}
                                </td>
                                <td style={tdStyle}>{parseFloat(p.target_amount || 0).toLocaleString()}</td>
                                <td style={tdStyle}>{p.usdc_balance != null ? `$${parseFloat(p.usdc_balance).toFixed(2)}` : parseFloat(p.current_amount || 0).toLocaleString()}</td>
                                <td style={tdStyle}>
                                    <span style={{
                                        display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
                                        background: p.is_active !== false ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)',
                                        color: p.is_active !== false ? '#4dd880' : '#ff6b6b',
                                    }}>
                                        {p.is_active !== false ? 'Active' : 'Disabled'}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <button style={btnStyle(p.is_active !== false ? 'muted' : 'success')} onClick={() => toggleActive(p)}>
                                            {p.is_active !== false ? 'Disable' : 'Enable'}
                                        </button>
                                        <button style={btnStyle('warning')} onClick={() => { setEditing({ ...p }); setShowCreateForm(false); }}>Edit</button>
                                        <button style={btnStyle('danger')} onClick={() => deletePool(p)}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
                                    {pools.length === 0 ? 'No pools found.' : 'No pools match your filter.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---- MONITOR TAB ----
function MonitorTab() {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [fetchError, setFetchError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        setFetchError('');
        api.get('/admin/test-runs')
            .then(res => setRuns(res.data))
            .catch(() => setFetchError('Failed to load test run history. Is the monitor service running?'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const fmtDuration = ms => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
    const fmtAge = ts => {
        const diff = Date.now() - new Date(ts).getTime();
        if (diff < 60000) return 'just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return `${Math.floor(diff / 86400000)}d ago`;
    };

    const latest = runs[0];
    const allGreen = latest && latest.failed === 0;

    return (
        <div>
            {/* Summary stat cards */}
            {latest && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
                    {[
                        {
                            label: 'Status',
                            value: allGreen ? '✓ All Passing' : `✗ ${latest.failed} Failing`,
                            color: allGreen ? '#4dd880' : '#ef4444',
                            bg: allGreen ? 'rgba(40,167,69,0.1)' : 'rgba(239,68,68,0.1)',
                            border: allGreen ? 'rgba(40,167,69,0.3)' : 'rgba(239,68,68,0.3)',
                        },
                        { label: 'Last Run', value: fmtAge(latest.ran_at) },
                        { label: 'Duration', value: fmtDuration(latest.duration_ms) },
                        { label: 'Total Runs', value: String(runs.length) },
                    ].map(({ label, value, color, bg, border }) => (
                        <div key={label} style={{
                            background: bg || 'rgba(255,255,255,0.04)',
                            border: `1px solid ${border || 'rgba(255,255,255,0.08)'}`,
                            borderRadius: '12px', padding: '14px 20px', minWidth: '130px',
                        }}>
                            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '5px' }}>{label}</div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: color || '#fff' }}>{value}</div>
                        </div>
                    ))}
                    <button onClick={load} style={{ ...btnStyle('muted'), marginLeft: 'auto', alignSelf: 'center' }}>↺ Refresh</button>
                </div>
            )}

            {/* Body */}
            {loading ? (
                <p style={{ color: 'rgba(255,255,255,0.4)', padding: '20px 0' }}>Loading…</p>
            ) : fetchError ? (
                <p style={{ color: '#ef4444', padding: '20px 0' }}>{fetchError}</p>
            ) : runs.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.4)', padding: '20px 0' }}>
                    No test runs recorded yet. The monitor service writes results every hour.
                </p>
            ) : (
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            {['Ran At', 'Status', 'Passed', 'Failed', 'Skipped', 'Duration'].map(h => (
                                <th key={h} style={thStyle}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map(run => {
                            // Normalize details: old rows stored a flat failed array; new rows store {passed, failed}
                            const det = !run.details ? { passed: [], failed: [] }
                                : Array.isArray(run.details) ? { passed: [], failed: run.details }
                                : run.details;
                            const allTests = [...(det.failed || []), ...(det.passed || [])];
                            const isOpen = expanded === run.id;
                            const accent = run.failed === 0 ? '#4dd880' : '#ef4444';
                            return (
                                <Fragment key={run.id}>
                                    <tr
                                        onClick={() => allTests.length > 0 && setExpanded(isOpen ? null : run.id)}
                                        style={{ cursor: allTests.length > 0 ? 'pointer' : 'default' }}
                                    >
                                        <td style={tdStyle}>{new Date(run.ran_at).toLocaleString()}</td>
                                        <td style={{ ...tdStyle, fontWeight: 700, color: accent }}>
                                            {run.failed === 0 ? '✓ Pass' : '✗ Fail'}
                                            {allTests.length > 0 && (
                                                <span style={{ fontSize: '0.78rem', fontWeight: 400, marginLeft: '6px', color: 'rgba(255,255,255,0.35)' }}>
                                                    {isOpen ? '▲' : '▼'} details
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ ...tdStyle, color: '#4dd880' }}>{run.passed}</td>
                                        <td style={{ ...tdStyle, color: run.failed > 0 ? '#ef4444' : 'rgba(255,255,255,0.3)' }}>{run.failed}</td>
                                        <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.3)' }}>{run.skipped}</td>
                                        <td style={tdStyle}>{fmtDuration(run.duration_ms)}</td>
                                    </tr>
                                    {isOpen && allTests.length > 0 && (
                                        <tr>
                                            <td colSpan={6} style={{
                                                ...tdStyle,
                                                background: run.failed === 0 ? 'rgba(40,167,69,0.05)' : 'rgba(239,68,68,0.06)',
                                                borderLeft: `3px solid ${accent}`,
                                                padding: '12px 20px',
                                            }}>
                                                {(det.failed || []).map((d, i) => (
                                                    <div key={`f${i}`} style={{ marginBottom: '6px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                                        <span style={{ color: '#ef4444', fontWeight: 700, flexShrink: 0 }}>✗</span>
                                                        <span style={{ color: '#ef4444', fontWeight: 600 }}>{d.title}</span>
                                                        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.83rem' }}>{d.error}</span>
                                                    </div>
                                                ))}
                                                {(det.passed || []).map((d, i) => (
                                                    <div key={`p${i}`} style={{ marginBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                                        <span style={{ color: '#4dd880', fontWeight: 700, flexShrink: 0 }}>✓</span>
                                                        <span style={{ color: 'rgba(255,255,255,0.65)' }}>{d.title}</span>
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ---- MAIN ADMIN PORTAL ----
function AdminPortal() {
    const [tab, setTab] = useState('users');

    const tabBtn = (id, label) => (
        <button
            onClick={() => setTab(id)}
            style={{
                padding: '8px 20px', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.9rem', borderRadius: '6px 6px 0 0',
                background: tab === id ? 'rgba(255,255,255,0.06)' : 'transparent',
                color: tab === id ? '#ffffff' : 'rgba(255,255,255,0.4)',
                borderBottom: tab === id ? '2px solid #FFC107' : '2px solid transparent',
                transition: 'all 0.2s ease',
            }}
        >
            {label}
        </button>
    );

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Gradient orbs */}
            <div style={{
                position: 'fixed', top: '-20%', right: '-10%',
                width: '500px', height: '500px',
                background: 'radial-gradient(circle, rgba(255,193,7,0.1) 0%, transparent 70%)',
                borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
            }} />
            <div style={{
                position: 'fixed', bottom: '-30%', left: '-10%',
                width: '400px', height: '400px',
                background: 'radial-gradient(circle, rgba(255,193,7,0.06) 0%, transparent 70%)',
                borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
            }} />

            <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '40px 20px', position: 'relative', zIndex: 10 }}>
                <div style={{ marginBottom: '28px' }}>
                    <h1 style={{
                        margin: '0 0 6px', fontSize: '1.8rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: '10px',
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.7) 100%)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    }}>
                        <span style={{ WebkitTextFillColor: 'initial' }}>🐝</span> YouBee Hive Admin
                    </h1>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem' }}>Manage users, assign admin roles, and control pools.</p>
                </div>

                <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '24px', display: 'flex', gap: '4px' }}>
                    {tabBtn('users', 'Users')}
                    {tabBtn('pools', 'Hives')}
                    {tabBtn('monitor', '🔍 Monitor')}
                </div>

                <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '16px',
                    padding: '24px',
                    backdropFilter: 'blur(20px)',
                }}>
                    {tab === 'users' && <UsersTab />}
                    {tab === 'pools' && <PoolsTab />}
                    {tab === 'monitor' && <MonitorTab />}
                </div>
            </div>
        </div>
    );
}

export default AdminPortal;
